<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="editingDiscount ? 'Edit Discount' : 'Create Discount'"
    modal
    :closable="true"
    style="width: min(560px, 96vw);"
  >
    <div class="form-grid">
      <!-- Code -->
      <div class="form-field full">
        <label class="Label">Code <span class="required">*</span></label>
        <div class="flex gap-2">
          <InputText
            v-model="form.code"
            placeholder="Promo Code"
            class="flex-1"
            variant="filled"
            size="small"
            :class="{ 'p-invalid': errors.code }"
            @input="form.code = form.code.toUpperCase()"
            :disabled="!!editingDiscount"
          />
          <Button
            v-if="!editingDiscount"
            icon="pi pi-sparkles"
            label="Generate"
            class="accent-button-transparent p-button-sm"
            style="white-space: nowrap; font-size: 12px;"
            @click="generateCode"
            type="button"
          />
        </div>
        <small v-if="errors.code" class="p-error">{{ errors.code }}</small>
      </div>

      <!-- Discount Type -->
      <div class="form-field">
        <label class="Label">Discount Type <span class="required">*</span></label>
        <Select
          v-model="form.discountType"
          :options="discountTypeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select type"
          class="w-full"
          variant="filled"
          size="small"
          :disabled="!!editingDiscount"
          :class="{ 'p-invalid': errors.discountType }"
        />
        <small v-if="errors.discountType" class="p-error">{{ errors.discountType }}</small>
      </div>

      <!-- Value -->
      <div class="form-field">
        <label class="Label">
          Value <span class="required">*</span>
          <span class="text-color-secondary Caption">
            ({{ form.discountType === 'percentage' ? '%' : 'LKR' }})
          </span>
        </label>
        <InputNumber
          v-model="form.value"
          :min="0"
          :max="form.discountType === 'percentage' ? 100 : undefined"
          :minFractionDigits="form.discountType === 'fixed' ? 2 : 0"
          :maxFractionDigits="form.discountType === 'fixed' ? 2 : 0"
          class="w-full"
          inputClass="w-full"
          variant="filled"
          size="small"
          :class="{ 'p-invalid': errors.value }"
        />
        <small v-if="errors.value" class="p-error">{{ errors.value }}</small>
      </div>

      <!-- Applies To -->
      <div class="form-field full">
        <label class="Label">Applies To <span class="required">*</span></label>
        <MultiSelect
          v-model="form.appliesTo"
          :options="appliesToOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select user types"
          class="w-full"
          variant="filled"
          size="small"
          :maxSelectedLabels="3"
          :class="{ 'p-invalid': errors.appliesTo }"
        />
        <small v-if="errors.appliesTo" class="p-error">{{ errors.appliesTo }}</small>
      </div>

      <!-- Duration — locked to "once" for now; remove :disabled and restore options when multi-duration support is needed -->
      <div class="form-field">
        <label class="Label">Duration</label>
        <Select
          v-model="form.duration"
          :options="durationOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          variant="filled"
          size="small"
          :disabled="true"
        />
      </div>

      <!-- TODO: remove comment below when multi-duration support is added -->
      <!-- Months Duration (only when repeating) -->
      <!-- <div class="form-field" v-if="form.duration === 'repeating'">
        <label class="Label">Months Duration</label>
        <InputNumber
          v-model="form.monthsDuration"
          :min="1"
          class="w-full"
          inputClass="w-full"
          variant="filled"
          size="small"
        />
      </div> -->

      <!-- Min Purchase -->
      <div class="form-field">
        <label class="Label">Min Purchase (LKR)</label>
        <InputNumber
          v-model="form.minPurchase"
          :min="0"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          class="w-full"
          inputClass="w-full"
          variant="filled"
          size="small"
          placeholder="0.00 = no minimum"
        />
      </div>

      <!-- Usage Limit -->
      <div class="form-field">
        <label class="Label">Usage Limit</label>
        <InputNumber
          v-model="form.usageLimit"
          :min="editingDiscount?.usedCount > 0 ? editingDiscount.usedCount : 1"
          class="w-full"
          inputClass="w-full"
          variant="filled"
          size="small"
          placeholder="Leave empty = unlimited"
          :class="{ 'p-invalid': errors.usageLimit }"
        />
        <small v-if="errors.usageLimit" class="p-error">{{ errors.usageLimit }}</small>
        <small v-else-if="editingDiscount?.usedCount > 0" class="text-color-secondary" style="font-size: 0.75rem; margin-top: 0.25rem;">
          Used {{ editingDiscount.usedCount }} time{{ editingDiscount.usedCount !== 1 ? 's' : '' }}, limit cannot be set lower
        </small>
      </div>

      <!-- Expiry Date -->
      <div class="form-field full">
        <label class="Label">Expiry Date</label>
        <DatePicker
          v-model="form.expiryDate"
          placeholder="No expiry"
          class="w-full"
          inputClass="w-full"
          variant="filled"
          size="small"
          showIcon
          :minDate="new Date()"
          dateFormat="yy-mm-dd"
        />
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" severity="secondary" class="accent-button-transparent" @click="$emit('update:visible', false)" />
      <Button
        :label="editingDiscount ? 'Update' : 'Create'"
        class="accent-button"
        @click="handleSubmit"
        :loading="isSubmitting"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import MultiSelect from "primevue/multiselect";
import DatePicker from "primevue/datepicker";

const props = defineProps({
  visible: Boolean,
  editingDiscount: { type: Object, default: null },
  isSubmitting: Boolean,
});

const emit = defineEmits(["update:visible", "submit"]);

const discountTypeOptions = [
  { label: "Percentage (%)", value: "percentage" },
  { label: "Fixed (LKR)", value: "fixed" },
];

const appliesToOptions = [
  { label: "Student", value: "student" },
  { label: "Tutor", value: "tutor" },
  { label: "Institute", value: "institute" },
];

// TODO: restore "forever" and "repeating" when multi-duration support is added
const durationOptions = [
  { label: "Once", value: "once" },
  // { label: "Forever", value: "forever" },
  // { label: "Repeating", value: "repeating" },
];

const emptyForm = () => ({
  code: "",
  discountType: "percentage",
  value: null,
  appliesTo: [],
  duration: "once",
  monthsDuration: null,
  minPurchase: null,
  usageLimit: null,
  expiryDate: null,
});

const form = ref(emptyForm());
const errors = ref({});

watch(
  () => props.visible,
  (open) => {
    if (!open) return;
    errors.value = {};
    if (props.editingDiscount) {
      const d = props.editingDiscount;
      form.value = {
        code: d.code,
        discountType: d.discountType,
        value: d.value,
        appliesTo: [...d.appliesTo],
        duration: d.duration ?? "once",
        monthsDuration: d.monthsDuration ?? null,
        minPurchase: d.minPurchase ?? null,
        usageLimit: d.usageLimit ?? null,
        expiryDate: d.expiryDate ? new Date(d.expiryDate) : null,
      };
    } else {
      form.value = emptyForm();
    }
  }
);

const generateCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  form.value.code = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  errors.value.code = "";
};

const validate = () => {
  const e = {};
  if (!form.value.code?.trim()) e.code = "Code is required.";
  else if (form.value.code.trim().length < 8) e.code = "Code must be at least 8 characters.";
  if (!form.value.discountType) e.discountType = "Discount type is required.";
  if (form.value.value === null || form.value.value === undefined) e.value = "Value is required.";
  if (!form.value.appliesTo?.length) e.appliesTo = "Select at least one user type.";
  const usedCount = props.editingDiscount?.usedCount ?? 0;
  if (form.value.usageLimit !== null && form.value.usageLimit !== undefined && form.value.usageLimit < usedCount) {
    e.usageLimit = `Limit cannot be less than current usage (${usedCount}).`;
  }
  errors.value = e;
  return Object.keys(e).length === 0;
};

const handleSubmit = () => {
  if (!validate()) return;

  const buildCommonFields = (payload) => {
    // TODO: remove comment when multi-duration support is added
    // if (form.value.duration === "repeating" && form.value.monthsDuration) {
    //   payload.monthsDuration = form.value.monthsDuration;
    // }
    if (form.value.minPurchase !== null && form.value.minPurchase !== undefined) {
      payload.minPurchase = form.value.minPurchase;
    }
    if (form.value.usageLimit !== null && form.value.usageLimit !== undefined) {
      payload.usageLimit = form.value.usageLimit;
    }
    if (form.value.expiryDate) {
      payload.expiryDate = new Date(form.value.expiryDate).toISOString();
    }
    return payload;
  };

  const payload = props.editingDiscount
    ? buildCommonFields({ value: form.value.value, appliesTo: form.value.appliesTo, duration: form.value.duration })
    : buildCommonFields({ code: form.value.code.trim(), discountType: form.value.discountType, value: form.value.value, appliesTo: form.value.appliesTo, duration: form.value.duration });

  emit("submit", payload);
};
</script>

<style scoped>
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding-top: 0.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  position: relative;
}

.form-field.full {
  grid-column: 1 / -1;
}

.form-field label {
  margin-bottom: 0.5rem;
}

.required {
  color: var(--red-500, #ef4444);
}

.p-error {
  position: absolute;
  top: 100%;
  left: 0;
  color: var(--red-500, #ef4444);
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-field.full {
    grid-column: 1;
  }
}
</style>
